import { Component, OnInit, OnDestroy } from '@angular/core';

import { SafeUrl, SafeResourceUrl } from '@angular/platform-browser';
import { IonicSafeString } from '@ionic/angular';

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, take, takeUntil } from 'rxjs/operators';

// components
import { AddNewsfeedComponent } from "../../components/add-newsfeed/add-newsfeed.component";

// services
import { CredentialService } from "../../services/credential.service";

// models
import { ParticipantCredential } from "../../models/participant-credential";

// pipes
import { SafePipe } from "../../pipes/string/safe";

import { log } from '../../helpers/log';

// extends
import { BasePage } from "../base";
import { environment } from 'src/environments/environment';
import { MatomoTracker } from 'ngx-matomo-client';

@Component({
    selector: 'app-credentials-wallet',
    templateUrl: './credentials-wallet.page.html',
    styleUrls: ['./credentials-wallet.page.scss'],
})
export class CredentialsWalletPage extends BasePage implements OnInit, OnDestroy {

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * user token for appointment download
     */
    public userToken: string;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * List of participant credentials
     *
     * @type {Array<ParticipantCredential>}
     */
    public credentials: Array<ParticipantCredential> = [];

    // handlers for events

    public countSizes = {
        2: [1, 2],
        3: [1, 2, 3],
        4: [1, 2, 3, 4],
        5: [1, 2, 3, 4, 5]
    };

    public showSharing: boolean = false;

    public defaultSharingOptions: Array<string> = [
        'linkedin',
        'twitter',
        // 'xing',
        // 'google',
        'whatsapp',
        'facebook',
        'email',
        'copy'
    ];

    public sharingOptions: Array<string> = [];

    /**
    * status for doing some action only with some credentials
    */
    public someSelected: boolean = false;

    /**
     * constructor
     *
     * @param credentialService
     */
    constructor(
        private safePipe: SafePipe,
        private tracker: MatomoTracker,
        public credentialService: CredentialService
    ) {
        super();
        // user language
        this.userLang = this.translate.currentLang;

        this.userToken = localStorage.getItem('token');

        this.sharingOptions = [...this.defaultSharingOptions];
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        this.userService.getCurrentUser()
            .pipe(
                takeUntil(this.ngUnsubscribe),
                debounceTime(0)
            ).subscribe((user) => {
                if (user.id) {
                    this.user = user;
                    this.event = this.user.selected_participant.event;

                    this.sharingOptions = [...this.defaultSharingOptions];
                    if (user.selected_participant && !user.selected_participant.invisible) {
                        // NOTE[jg] - add skillsclub sharing
                        this.sharingOptions = ['skillsclub', ...this.defaultSharingOptions];
                    }
                }
            });

    }

    ionViewWillEnter() {
        // console.info('view will enter appointments');
        this.userService.getCurrentUser()
            .pipe(
                take(1),
                debounceTime(0)
            ).subscribe(
                (user) => {
                    if (user.id) {
                        this.user = user;
                        this.event = this.user.selected_participant.event;

                        this.sharingOptions = [...this.defaultSharingOptions];

                        if (user.selected_participant && !user.selected_participant.invisible) {
                            this.sharingOptions = ['skillsclub', ...this.defaultSharingOptions];
                        }
                        this.refresh();
                    } else {
                        // reset credentials
                        this.credentials = [];
                    }
                });
    }

    refresh($event?) {
        // load participant timeslot stored setting
        this.credentialService.getParticipantCredentials(this.user.selected_participant.id).subscribe(
            (data: {
                credentials: Array<ParticipantCredential>,
                processing: boolean
            }) => {

                this.credentials = [];

                data.credentials.forEach(
                    (credential) => {
                        this.credentials.push(new ParticipantCredential(credential));
                    });

                // hide loading
                if ($event) {
                    $event.target.complete();
                } else {
                    this.loading = false;
                }

                if (data.processing) {
                    this.overlayService.showSuccess(this.translate.instant('CREDENTIAL_WALLET_MORE_RECORDS_PROCESSING'));
                }
            },
            (error) => {
                // TODO
                this.showError(error);
            });
    }

    openPublicDetail(credential: ParticipantCredential) {
        let url = '/credential/' + credential.id + '-' + credential.token;
        // in native mobile app open new browser window with proper details link
        if (this.plt.is('cordova')) {
            window.open(environment.pwa + url, '_system');
        } else {
            window.open(location.origin + url, '_blank');
        }
    }

    showShare(credential: ParticipantCredential) {

        this.tracker.trackEvent('Credential Share', 'open');

        credential.showSharing = true;
        this.showSharing = true;
        return false;
    }

    share(type: string, credential: ParticipantCredential, badge: boolean = true) {

        credential.showSharing = false;
        this.showSharing = false;

        let origin = location.origin;
        if (this.plt.is('cordova')) {
            origin = environment.pwa;
        }

        let link = origin + '/credential/' + credential.id + '-' + credential.token + '?locale=' + this.userLang + (badge ? '&badge=true' : '');

        let share = {
            link: link,
            text: '',//this.translate.instant('CREDENTIAL_WALLET_SHARE_TEXT'),
            subject: this.translate.instant('CREDENTIAL_WALLET_SHARE_SUBJECT'),
        };


        if (!sessionStorage.getItem(type + credential.id)) {
            this.tracker.trackEvent('Credential Share', type);
            sessionStorage.setItem(type + credential.id, '1');
        }

        switch (type) {
            case 'skillsclub':
                this.addNewsfedd({
                    link: share.link,
                    text: share.text,
                    subject: share.subject
                }, credential, badge);
                break;
            default:
                this.plt.shareLink(type, share);
        }
    }

    /**
     * open modal with newsfeed add form
     * @return void
     */
    public async addNewsfedd(
        recommendation: { link: string, text: string, subject: string },
        credential: ParticipantCredential,
        badge: boolean = true
    ): Promise<void> {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AddNewsfeedComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                modal: true,
                displayBigTextarea: true,
                showKeywords: this.event.matching_status,
                event: this.event,
                user: this.user,
                message: recommendation.text,
                link: recommendation.link,
                credentialSharing: true,
                image_url: this.plt.extractLinkFromSigned(credential.certification_preview && !badge ? credential.certification_preview : credential.certification_badge)
            }
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    /**
     * hide sharing
     *
     */
    hideSharing(): void {
        this.showSharing = false;
        this.credentials.forEach((c) => {
            c.showSharing = false;
        })
    }

    /**
     * change selected items status for selection
     *
     */
    toggle(credential: ParticipantCredential): void {
        credential.selected = !credential.selected;
        // check if some credentials are selected
        this.someSelected = this.credentials.filter((c) => { return c.selected }).length > 0;
    }

    /**
     * change selected items status for selection
     *
     */
    shareLink(): void {
        let selected = this.credentials.filter(c => c.selected).map(c => c.id);

        // load JWT token for authorization of selected list of credentials
        this.credentialService.getParticipantCredentialsToken(this.user.selected_participant_id, selected).subscribe(
            (success: { token: string }) => {
                let link = (this.plt.is('cordova') ? environment.pwa : location.origin) + '/credentials/' + encodeURIComponent(success.token);
                this.showShareLink(link);
            }, (error) => {

            }
        );
    }

    /**
     * show alert with qr code + link for sharing selected credentials or whole wallet
     *
     */
    public async showShareLink(link: string): Promise<void> {

        this.tracker.trackEvent('Credential Share', 'wallet');

        let qrcode = this.plt.baseLinkPath + this.plt.apiPrefix + '/event/qr-code/generate?link=' + encodeURIComponent(link);
        // qrcode = 'https://server.congreet.com/api/v2/event/qr-code/generate?link=' + encodeURIComponent(link);

        this.overlayService.showConfirm(
            this.translate.instant('CREDENTIAL_WALLET_COPY_LINK'),
            // for cordova show only image without download option
            (this.plt.is('cordova') ?
                new IonicSafeString('<div class="qrcode"><img src="' + qrcode + '" /></div>'
                    + link)
                : new IonicSafeString('<a href="' + qrcode
                    + '" title="' + this.translate.instant('CREDENTIAL_WALLET_LINK_QR_TITLE')
                    + '" download="' + this.translate.instant('CREDENTIAL_WALLET_LINK_QR_TITLE')
                    + '" target="_blank" class="qrcode"><img src="' + qrcode + '" /></a><br />'
                    + link)),
            () => {
                // copy to clickboard 
                this.plt.copyToClipboard(link);
                this.showSuccess(this.translate.instant('LINK_COPIED'));
                // unselect  all credentials
                this.credentials.forEach((c) => { c.selected = false });
            },
            () => {
                // unselect  all credentials
                this.credentials.forEach((c) => { c.selected = false });
            },
            this.translate.instant('CREDENTIAL_WALLET_COPY_LINK_BUTTON'),
            this.translate.instant('BUTTON_CANCEL')
        );
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
